import React, { useState, useEffect } from 'react';
import { activityAPI } from '../lib/api';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Activity, UserPlus, UserMinus, RefreshCw, ArrowRightLeft, Eye, Edit, Trash2, FolderPlus } from 'lucide-react';

const ACTION_ICONS = {
  invite_sent: UserPlus,
  invite_resent: RefreshCw,
  invite_accepted: UserPlus,
  invite_cancelled: UserMinus,
  member_removed: UserMinus,
  ownership_transferred: ArrowRightLeft,
  project_viewed: Eye,
  project_updated: Edit,
  project_created: FolderPlus,
  task_created: FolderPlus,
  task_updated: Edit,
  task_deleted: Trash2,
};

const ACTION_COLORS = {
  invite_sent: 'bg-blue-100 text-blue-600',
  invite_resent: 'bg-orange-100 text-orange-600',
  invite_accepted: 'bg-green-100 text-green-600',
  invite_cancelled: 'bg-red-100 text-red-600',
  member_removed: 'bg-red-100 text-red-600',
  ownership_transferred: 'bg-purple-100 text-purple-600',
  project_viewed: 'bg-zinc-100 text-zinc-600',
  project_updated: 'bg-blue-100 text-blue-600',
  project_created: 'bg-green-100 text-green-600',
  task_created: 'bg-green-100 text-green-600',
  task_updated: 'bg-blue-100 text-blue-600',
  task_deleted: 'bg-red-100 text-red-600',
};

function formatTimeAgo(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default function ActivityLog({ projectId, showProjectName = false }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();
  }, [projectId]);

  const loadActivities = async () => {
    try {
      let res;
      if (projectId) {
        res = await activityAPI.getProjectActivity(projectId);
      } else {
        res = await activityAPI.getAllActivity();
      }
      setActivities(res.data.activities || []);
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="bg-white border-zinc-200 rounded-xl p-6">
      <h3 className="font-outfit font-semibold text-lg text-zinc-900 mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5 text-primary" />
        Activity Log
      </h3>
      
      {activities.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">
          <Activity className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
          <p>No activity recorded yet</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {activities.map((activity) => {
            const IconComponent = ACTION_ICONS[activity.action] || Activity;
            const colorClass = ACTION_COLORS[activity.action] || 'bg-zinc-100 text-zinc-600';
            
            return (
              <div 
                key={activity.id} 
                className="flex items-start gap-3 p-3 bg-zinc-50 rounded-lg"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                  <IconComponent className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-zinc-900">{activity.user_name}</span>
                    <Badge variant="outline" className="text-xs">
                      {activity.action.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <p className="text-sm text-zinc-600 mt-0.5">{activity.details}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-400">{formatTimeAgo(activity.timestamp)}</span>
                    {showProjectName && activity.project_name && (
                      <span className="text-xs text-zinc-500">• {activity.project_name}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
